import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { ApiError, apiGet, apiSend, serverBesked } from "../../lib/customFetch";
import styles from "./AbsenceAdmin.module.css";

const adminRequestSchema = z
  .object({
    id: z.uuid(),
    medarbejderNavn: z.string().min(1),
    type: z.enum(["ferie", "sygdom", "fri"]),
    fraDato: z.iso.date(),
    tilDato: z.iso.date(),
    note: z.string().nullable(),
    status: z.enum(["afventer", "godkendt", "afslaaet", "annulleret"]),
    afgoerelseNote: z.string().nullable(),
    afgjortAt: z.iso.datetime({ offset: true }).nullable(),
    annulleretAt: z.iso.datetime({ offset: true }).nullable(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
  })
  .strict();
const adminRequestsSchema = z.array(adminRequestSchema);

type AdminRequest = z.infer<typeof adminRequestSchema>;
type Decision = "godkend" | "afslaa";

const TYPE_LABEL: Record<AdminRequest["type"], string> = {
  ferie: "Ferie",
  sygdom: "Sygdom",
  fri: "Fri",
};
const STATUS_LABEL: Record<AdminRequest["status"], string> = {
  afventer: "Afventer",
  godkendt: "Godkendt",
  afslaaet: "Afslået",
  annulleret: "Annulleret af medarbejderen",
};
const dateFormat = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function formatDate(value: string): string {
  return dateFormat.format(new Date(`${value}T00:00:00Z`));
}

function interval(request: AdminRequest): string {
  const from = formatDate(request.fraDato);
  return request.fraDato === request.tilDato
    ? from
    : `${from} – ${formatDate(request.tilDato)}`;
}

function fallbackProblem(error: unknown, fallback: string): string {
  return error instanceof ApiError ? serverBesked(error) ?? fallback : fallback;
}

export function AbsenceAdmin() {
  const [requests, setRequests] = useState<AdminRequest[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [active, setActive] = useState<{ id: string; decision: Decision } | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(() => {
    setLoadError(false);
    setConfirmation(null);
    apiGet<unknown>("/api/clockin/admin/absence-requests")
      .then((payload) => setRequests(adminRequestsSchema.parse(payload)))
      .catch(() => {
        setRequests(null);
        setLoadError(true);
      });
  }, []);

  useEffect(() => load(), [load]);
  useEffect(() => {
    if (active !== null) noteRef.current?.focus();
  }, [active]);

  const begin = (id: string, decision: Decision) => {
    setActive({ id, decision });
    setNote("");
    setActionError(null);
    setConfirmation(null);
  };

  const updateRequest = (updated: AdminRequest) => {
    setRequests((current) =>
      current?.map((request) => (request.id === updated.id ? updated : request)) ?? [],
    );
  };

  const decide = async () => {
    if (active === null || busy) return;
    const cleanNote = note.trim();
    if (cleanNote === "") {
      setActionError("Skriv en kort note til medarbejderen.");
      noteRef.current?.focus();
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const payload = await apiSend<unknown>(
        "POST",
        `/api/clockin/admin/absence-requests/${active.id}/decision`,
        { decision: active.decision, note: cleanNote },
        { signal: AbortSignal.timeout(15_000) },
      );
      const updated = adminRequestSchema.parse(payload);
      updateRequest(updated);
      setConfirmation(
        active.decision === "godkend"
          ? `${updated.medarbejderNavn}s fravær er godkendt og registreret.`
          : `${updated.medarbejderNavn}s anmodning er afslået.`,
      );
      setActive(null);
      setNote("");
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 409) {
        try {
          const refreshed = adminRequestsSchema.parse(
            await apiGet<unknown>("/api/clockin/admin/absence-requests"),
          );
          setRequests(refreshed);
        } catch {
          // Bevar den senest kendte kø; den konkrete serverbesked er stadig sand.
        }
        if (error.code === "allerede_afgjort") {
          setActive(null);
          setConfirmation("Anmodningen var allerede afsluttet. Køen er opdateret.");
        }
      }
      setActionError(
        fallbackProblem(error, "Kunne ikke afgøre anmodningen — prøv igen."),
      );
    } finally {
      setBusy(false);
    }
  };

  const pending = requests?.filter((request) => request.status === "afventer") ?? [];
  const history = requests?.filter((request) => request.status !== "afventer") ?? [];

  const renderRequest = (request: AdminRequest, actionable: boolean) => (
    <li key={request.id} className={styles.request}>
      <div className={styles.requestTop}>
        <div>
          <strong>{request.medarbejderNavn}</strong>
          <p>{TYPE_LABEL[request.type]} · {interval(request)}</p>
        </div>
        <span className={styles.status} data-status={request.status}>
          {STATUS_LABEL[request.status]}
        </span>
      </div>
      {request.note !== null && <p className={styles.note}>Medarbejderens note: {request.note}</p>}
      {request.afgoerelseNote !== null && (
        <p className={styles.note}>Afgørelsesnote: {request.afgoerelseNote}</p>
      )}
      {actionable && active?.id !== request.id && (
        <div className={styles.actions}>
          <button type="button" className={styles.approve} onClick={() => begin(request.id, "godkend")}>
            Godkend
          </button>
          <button type="button" className={styles.reject} onClick={() => begin(request.id, "afslaa")}>
            Afslå
          </button>
        </div>
      )}
      {actionable && active?.id === request.id && (
        <div className={styles.decisionForm}>
          <label htmlFor={`absence-decision-note-${request.id}`}>
            {active.decision === "godkend"
              ? "Note ved godkendelse (påkrævet)"
              : "Begrundelse for afslag (påkrævet)"}
          </label>
          <textarea
            ref={noteRef}
            id={`absence-decision-note-${request.id}`}
            rows={2}
            maxLength={500}
            required
            aria-required="true"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          {actionError !== null && <p className={styles.error} role="alert">{actionError}</p>}
          <div className={styles.actions}>
            <button type="button" className={active.decision === "godkend" ? styles.approve : styles.reject} disabled={busy} onClick={() => void decide()}>
              {busy
                ? "Gemmer…"
                : active.decision === "godkend"
                  ? "Bekræft godkendelse"
                  : "Bekræft afslag"}
            </button>
            <button type="button" className={styles.cancel} onClick={() => setActive(null)}>
              Fortryd
            </button>
          </div>
        </div>
      )}
    </li>
  );

  return (
    <section className={styles.panel} aria-labelledby="absence-admin-title">
      <header className={styles.heading}>
        <div>
          <h2 id="absence-admin-title">Fraværsanmodninger</h2>
          <p>Godkendt fravær registreres med det samme i tid, rapport og eksport.</p>
        </div>
        <button type="button" className={styles.refresh} onClick={load}>Opdatér</button>
      </header>

      {confirmation !== null && <p className={styles.confirmation} role="status">{confirmation}</p>}
      {loadError ? (
        <div className={styles.state} role="alert">
          <p>Kunne ikke hente fraværsanmodninger.</p>
          <button type="button" onClick={load}>Prøv igen</button>
        </div>
      ) : requests === null ? (
        <p className={styles.state} role="status">Henter fraværsanmodninger…</p>
      ) : (
        <>
          <section className={styles.group} aria-labelledby="absence-pending-title">
            <h3 id="absence-pending-title">Til afgørelse <span>{pending.length}</span></h3>
            {pending.length === 0 ? (
              <p className={styles.state}>Der er ingen anmodninger, som venter.</p>
            ) : (
              <ul className={styles.list} aria-label="Fravær til afgørelse">
                {pending.map((request) => renderRequest(request, true))}
              </ul>
            )}
          </section>
          <section className={styles.group} aria-labelledby="absence-history-title">
            <h3 id="absence-history-title">Historik</h3>
            {history.length === 0 ? (
              <p className={styles.state}>Der er ingen afsluttede anmodninger endnu.</p>
            ) : (
              <ul className={styles.list} aria-label="Afsluttede fraværsanmodninger">
                {history.map((request) => renderRequest(request, false))}
              </ul>
            )}
          </section>
        </>
      )}
    </section>
  );
}
